/**
 * useBrowserNotifications
 *
 * Gestisce le notifiche OS native (Notification API).
 * - Richiede permesso solo se l'utente ha abilitato `browser_notifications`
 *   nelle preferenze (o al primo accesso se non c'è preferenza)
 * - Mostra una notifica OS per ogni nuova notifica realtime non letta
 * - Non mostra nulla se la finestra è già in focus (l'utente sta già guardando)
 *
 * Uso: chiamare in App.jsx (dentro il Router e il NotificationProvider).
 */
import { useEffect, useRef } from 'react';
import { useNotifications } from '../context/NotificationContext';

const TYPE_EMOJI = {
    broadcast:       '📢',
    past_due:        '⚠️',
    deadline:        '📅',
    new_sub:         '🎉',
    payment_ok:      '✅',
    new_contract:    '📝',
    request_pending: '📋',
    message:         '💬',
};

export function useBrowserNotifications() {
    const { notifications, preferences } = useNotifications();
    const prevIdsRef  = useRef(null);
    const initialized = useRef(false);

    // Richiedi permesso se le preferenze lo indicano
    useEffect(() => {
        if (preferences?.browser_notifications && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, [preferences?.browser_notifications]);

    useEffect(() => {
        if (notifications.length === 0) return;

        if (!initialized.current) {
            prevIdsRef.current = new Set(notifications.map(n => n.id));
            initialized.current = true;
            return;
        }

        const currentIds = new Set(notifications.map(n => n.id));
        const prevIds    = prevIdsRef.current || new Set();

        // Non mostrare nulla se la finestra è in focus o il permesso non è granted
        if (document.hasFocus()) {
            prevIdsRef.current = currentIds;
            return;
        }
        if (Notification.permission !== 'granted') {
            prevIdsRef.current = currentIds;
            return;
        }
        if (!preferences?.browser_notifications) {
            prevIdsRef.current = currentIds;
            return;
        }

        const newNotifs = notifications.filter(n => !prevIds.has(n.id) && !n.read);

        newNotifs.forEach(n => {
            const emoji = TYPE_EMOJI[n.type] || '🔔';
            const notif = new Notification(`${emoji} ${n.title}`, {
                body:  n.message?.slice(0, 120) || '',
                icon:  '/favicon.ico',
                tag:   n.id,    // Evita duplicati per la stessa notifica
            });

            // Click sulla notifica OS → porta l'utente alla pagina giusta
            notif.onclick = () => {
                window.focus();
                if (n.link) window.location.href = n.link;
            };
        });

        prevIdsRef.current = currentIds;
    }, [notifications, preferences]);
}
